import BigNumber from 'bignumber.js';

import { FormattedMessage } from '@flipster/i18n';
import { Styles, css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import TradeUpIcon from '@flipster/ui/assets/svgs/unfilled/trade-up.svg';
import Num from '@flipster/utils/num';

interface SymbolOpenPositionPercentProps {
  openPositionStat: {
    longPositionPct: BigNumber;
    shortPositionPct: BigNumber;
    updatedAt: string;
  };
  css?: Styles;
}
export const SymbolOpenPositionPercent = ({ openPositionStat, css: cssProps }: SymbolOpenPositionPercentProps) => {
  return <TrendBar long={openPositionStat.longPositionPct} short={openPositionStat.shortPositionPct} css={cssProps} />;
};

interface TrendBarProps {
  long: BigNumber;
  short: BigNumber;
  css: Styles;
}
const TrendBar = ({ long, short, css: cssProps }: TrendBarProps) => {
  const roundedLong = removeDecimalOnEdge(long.toFixed(1, BigNumber.ROUND_HALF_UP));
  const roundedShort = removeDecimalOnEdge(short.toFixed(1, BigNumber.ROUND_HALF_DOWN));
  const dominate = long.comparedTo(short);
  return (
    <div className={css(stack.raw({ gap: 8 }), cssProps)}>
      <div className={hstack({ rounded: 999, h: 6, overflow: 'hidden' })}>
        <div
          className={css({ h: 6, bg: 'background.accent.buy.tint' }, dominate >= 0 && { bg: 'content.state.positive' })}
          style={{ flex: roundedLong }}
        />
        <div
          className={css(
            { h: 6, bg: 'background.accent.sell.tint' },
            dominate <= 0 && { bg: 'content.state.negative' },
          )}
          style={{ flex: roundedShort }}
        />
      </div>
      <div className={hstack({ textStyle: 'Text/M/Monospace/Regular', justifyContent: 'space-between' })}>
        <div
          className={css(
            hstack.raw({ color: 'content.state.positive', gap: 2 }),
            dominate === 1 && { textStyle: 'Text/M/Monospace/Strong' },
          )}
        >
          <TradeUpIcon />
          <p>
            <FormattedMessage
              id='Autogen_common.r251126.s_Long_roundedLong'
              values={{
                roundedLong: roundedLong,
              }}
            />
          </p>
        </div>
        <div className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-1' })}>VS</div>
        <div
          className={css(
            hstack.raw({ color: 'content.state.negative', gap: 2 }),
            dominate === -1 && { textStyle: 'Text/M/Monospace/Strong' },
          )}
        >
          <p>
            <FormattedMessage
              id='Autogen_common.r251126.s_Short_roundedShort'
              values={{
                roundedShort: roundedShort,
              }}
            />
          </p>
          <TradeUpIcon className={css({ transform: 'rotate(90deg)' })} />
        </div>
      </div>
    </div>
  );
};

function removeDecimalOnEdge(numeric: string) {
  const bigNumber = Num(numeric);
  if (bigNumber.eq(100) || bigNumber.eq(0)) return bigNumber.toFixed(0);
  return numeric;
}
